// 国家详情页「数据层」助手:全部由现有 countries.json 计算,零内容风险,多语言靠 i18n 模板。
import { countries, byCca3, type Country, type Lang } from './countries';
import slugData from '../data/country-slugs.json';
import geoTerms from '../data/geo-terms.json';

const GEO = geoTerms as Record<string, Record<Lang, string>>;
/** region / subregion 名本地化(数据里是英文)。 */
export function geoTerm(name: string, lang: Lang): string {
  return GEO[name]?.[lang] ?? name;
}

const SLUGS = slugData as Record<string, Record<Lang, string>>;
const LANGS: Lang[] = ['en', 'es', 'fr', 'de', 'pt', 'it', 'tr'];

// slug ↔ cca3
export function slugOf(cca3: string, lang: Lang): string {
  return SLUGS[cca3]?.[lang] ?? cca3.toLowerCase();
}
const slugToCca3: Record<Lang, Map<string, string>> = Object.fromEntries(
  LANGS.map((l) => [l, new Map(Object.entries(SLUGS).map(([cca3, m]) => [m[l], cca3]))]),
) as Record<Lang, Map<string, string>>;
export function bySlug(lang: Lang, slug: string): Country | undefined {
  const cca3 = slugToCca3[lang]?.get(slug);
  return cca3 ? byCca3(cca3) : undefined;
}

// 预排序
const byPop = [...countries].sort((a, b) => b.population - a.population);
const byArea = [...countries].sort((a, b) => b.area - a.area);
const popRankMap = new Map(byPop.map((c, i) => [c.cca3, i + 1]));
const areaRankMap = new Map(byArea.map((c, i) => [c.cca3, i + 1]));

export const popRank = (c: Country) => popRankMap.get(c.cca3) ?? 0;
export const areaRank = (c: Country) => areaRankMap.get(c.cca3) ?? 0;
export const density = (c: Country) => (c.area > 0 ? c.population / c.area : 0);

export function regionPopRank(c: Country): { rank: number; total: number } {
  const inRegion = byPop.filter((x) => x.region === c.region);
  return { rank: inRegion.findIndex((x) => x.cca3 === c.cca3) + 1, total: inRegion.length };
}

// 知名国集合(用于"比 X 大/小",避免排序相邻的冷门国)
const NOTABLE = new Set([
  'USA', 'CHN', 'IND', 'RUS', 'BRA', 'CAN', 'AUS', 'ARG', 'KAZ', 'DZA', 'MEX', 'IDN', 'SAU', 'ZAF',
  'FRA', 'ESP', 'DEU', 'GBR', 'ITA', 'JPN', 'KOR', 'TUR', 'EGY', 'NGA', 'ETH', 'KEN', 'IRN', 'IRQ',
  'PAK', 'BGD', 'VNM', 'THA', 'PHL', 'MYS', 'UKR', 'POL', 'SWE', 'NOR', 'FIN', 'GRC', 'PRT', 'NLD',
  'BEL', 'CHE', 'AUT', 'CHL', 'COL', 'PER', 'VEN', 'CUB', 'MAR', 'NZL', 'SGP', 'ISR', 'ARE',
]);

/** 在知名国里找"刚好比它大 / 刚好比它小"的国(按 metric)。返回国名前用 localName。 */
export function notableNeighbors(c: Country, metric: 'area' | 'population'): { bigger?: Country; smaller?: Country } {
  const v = c[metric];
  const pool = countries.filter((x) => NOTABLE.has(x.cca3) && x.cca3 !== c.cca3);
  let bigger: Country | undefined; // 比 c 大的里最接近的
  let smaller: Country | undefined; // 比 c 小的里最接近的
  for (const x of pool) {
    if (x[metric] > v) {
      if (!bigger || x[metric] < bigger[metric]) bigger = x;
    } else if (x[metric] < v) {
      if (!smaller || x[metric] > smaller[metric]) smaller = x;
    }
  }
  return { bigger, smaller };
}

export function neighbors(c: Country): Country[] {
  return c.borders.map((b) => byCca3(b)).filter((x): x is Country => !!x);
}

/** 相关国家:同 subregion 优先,补同 region;排除自身与邻国。 */
export function relatedCountries(c: Country, n = 5): Country[] {
  const nb = new Set([c.cca3, ...c.borders]);
  const sameSub = countries.filter((x) => x.subregion === c.subregion && !nb.has(x.cca3));
  const sameReg = countries.filter((x) => x.region === c.region && x.subregion !== c.subregion && !nb.has(x.cca3));
  const pick = [...sameSub, ...sameReg].sort((a, b) => b.population - a.population);
  return pick.slice(0, n);
}

export function totalCountries(): number {
  return countries.length;
}

/** 序数格式化(按语言)。en: 23rd · fr: 23e/1er · de: 23. · es/pt: 23.º */
export function ordinal(n: number, lang: Lang): string {
  if (lang === 'fr') return n === 1 ? '1er' : `${n}e`;
  if (lang === 'de') return `${n}.`;
  if (lang === 'es' || lang === 'pt') return `${n}.º`;
  if (lang === 'it') return `${n}º`;
  if (lang === 'tr') return `${n}.`;
  const v = n % 100;
  const s = ['th', 'st', 'nd', 'rd'];
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
